import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { MessageSquare, X, Sparkles } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { api } from '../services/api';

interface ContentViewerProps {
  content: string;
  onAskQuestion?: (selectedText: string, question: string) => void;
}

interface SelectionPopupState {
  visible: boolean;
  x: number;
  y: number;
  selectedText: string;
}

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const latexToHtml = (latex: string, displayMode: boolean): string => {
  try {
    return katex.renderToString(latex.trim(), {
      displayMode,
      throwOnError: false,
      strict: false,
      trust: false,
      output: 'html',
    });
  } catch {
    return `<code class="ds-math-error">${escapeHtml(latex.trim())}</code>`;
  }
};

const summarizePracticePanel = (element: HTMLElement, fallback: string): string => {
  const title = element.querySelector<HTMLElement>('.ds-component-title')?.textContent?.trim();
  if (title) return title;
  const heading = element.querySelector<HTMLElement>('h3, h4')?.textContent?.trim();
  return heading || fallback;
};

const normalizePracticePanel = (doc: Document, element: HTMLElement, fallbackTitle: string) => {
  if (element.tagName === 'DETAILS' || element.closest('details.ds-practice-panel')) return;

  const details = doc.createElement('details');
  details.className = `${element.className} ds-practice-panel`;
  Array.from(element.attributes).forEach((attr) => {
    if (attr.name !== 'class') details.setAttribute(attr.name, attr.value);
  });

  const summary = doc.createElement('summary');
  summary.className = 'ds-practice-summary';
  summary.textContent = summarizePracticePanel(element, fallbackTitle);
  details.append(summary);

  while (element.firstChild) details.append(element.firstChild);
  element.replaceWith(details);
};

const normalizeLessonStructure = (doc: Document) => {
  doc.querySelectorAll<HTMLElement>('.ds-code-runner').forEach((element) => {
    if (element.classList.contains('nb-code')) return;
    normalizePracticePanel(doc, element, 'Coding Practice');
  });
  doc.querySelectorAll<HTMLElement>('.ds-quiz-choice, .ds-quiz').forEach((element) => {
    normalizePracticePanel(doc, element, 'Knowledge Check');
  });
  doc.querySelectorAll<HTMLElement>('.ds-short-answer').forEach((element) => {
    normalizePracticePanel(doc, element, 'Short Answer Practice');
  });
};

const renderMathInHtml = (html: string): string => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const skipTags = new Set(['CODE', 'PRE', 'TEXTAREA', 'SCRIPT', 'STYLE']);
  const pattern = /(\\\((.+?)\\\)|\\\[(.+?)\\\]|\$\$(.+?)\$\$)/gs;

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    const parent = node.parentElement;
    if (!parent || parent.closest('code, pre, textarea, script, style')) continue;
    if (skipTags.has(parent.tagName)) continue;
    if (pattern.test(node.nodeValue || '')) textNodes.push(node);
    pattern.lastIndex = 0;
  }

  textNodes.forEach((node) => {
    const text = node.nodeValue || '';
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    text.replace(pattern, (match, _all, inlineContent, bracketContent, dollarContent, index) => {
      if (index > lastIndex) fragment.append(doc.createTextNode(text.slice(lastIndex, index)));
      const content = inlineContent || bracketContent || dollarContent || '';
      const span = doc.createElement('span');
      const displayMode = Boolean(bracketContent || dollarContent);
      span.className = displayMode ? 'ds-math-render ds-math-block' : 'ds-math-render';
      span.innerHTML = latexToHtml(content, displayMode);
      fragment.append(span);
      lastIndex = index + match.length;
      return match;
    });
    if (lastIndex < text.length) fragment.append(doc.createTextNode(text.slice(lastIndex)));
    node.replaceWith(fragment);
  });

  normalizeLessonStructure(doc);

  return doc.body.innerHTML;
};

export default function ContentViewer({ content, onAskQuestion }: ContentViewerProps) {
  const renderedContent = useMemo(() => renderMathInHtml(content), [content]);
  const [popup, setPopup] = useState<SelectionPopupState>({
    visible: false,
    x: 0,
    y: 0,
    selectedText: '',
  });
  const [question, setQuestion] = useState('');
  const [isAsking, setIsAsking] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && text.length > 0 && text.split(' ').length <= 200) {
      const range = selection?.getRangeAt(0);
      const rect = range?.getBoundingClientRect();

      if (rect && contentRef.current) {
        const contentRect = contentRef.current.getBoundingClientRect();
        setPopup({
          visible: true,
          x: rect.left - contentRect.left + rect.width / 2,
          y: rect.top - contentRect.top - 50,
          selectedText: text,
        });
      }
    } else {
      setPopup(prev => ({ ...prev, visible: false }));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        // Check if the click is on the popup or its children
        setPopup(prev => ({ ...prev, visible: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    root.querySelectorAll<HTMLTextAreaElement>('textarea[data-save-key]').forEach((textarea) => {
      if (textarea.dataset.enhanced === 'true') return;
      textarea.dataset.enhanced = 'true';
      const key = textarea.dataset.saveKey;
      if (!key) return;
      const stored = localStorage.getItem(`ds101-answer-${key}`);
      if (stored !== null && !textarea.value) textarea.value = stored;
      textarea.addEventListener('input', () => {
        localStorage.setItem(`ds101-answer-${key}`, textarea.value);
      });
    });

    root.querySelectorAll<HTMLButtonElement>('[data-copy-code]').forEach((button) => {
      if (button.dataset.enhanced === 'true') return;
      button.dataset.enhanced = 'true';
      button.addEventListener('click', async () => {
        const container = button.closest('.nb-code, .ds-code-runner');
        const code = container?.querySelector('pre code')?.textContent || '';
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          const original = button.textContent || 'Copy code';
          button.textContent = 'Copied';
          window.setTimeout(() => {
            button.textContent = original;
          }, 1200);
        } catch {
          button.textContent = 'Copy failed';
        }
      });
    });

    root.querySelectorAll<HTMLElement>('.ds-code-runner').forEach((runner) => {
      if (runner.dataset.enhanced === 'true') return;
      runner.dataset.enhanced = 'true';

      const codeElement = runner.querySelector('pre code') || runner.querySelector('code');
      const initialCode = codeElement?.textContent || '';
      const originalPre = codeElement?.closest('pre');
      if (originalPre && !runner.classList.contains('nb-code')) {
        originalPre.classList.add('ds-original-code');
      }

      const editorShell = document.createElement('div');
      editorShell.className = 'ds-code-editor-shell';

      const textarea = document.createElement('textarea');
      textarea.className = 'ds-code-input';
      textarea.value = initialCode;
      textarea.spellcheck = false;

      const toolbar = document.createElement('div');
      toolbar.className = 'ds-code-toolbar';

      const label = document.createElement('span');
      label.textContent = runner.dataset.language || 'python';

      const runButton = document.createElement('button');
      runButton.type = 'button';
      runButton.className = 'ds-run-button';
      runButton.textContent = 'Run';

      const output = document.createElement('pre');
      output.className = 'ds-code-output';
      output.textContent = 'Run the code to see output here.';

      toolbar.append(label, runButton);
      editorShell.append(toolbar, textarea, output);
      runner.append(editorShell);

      runButton.addEventListener('click', async () => {
        runButton.textContent = 'Running...';
        runButton.setAttribute('disabled', 'true');
        output.textContent = '';
        output.classList.remove('error');
        try {
          const notebook = runner.closest('.notebook');
          const codeToRun = notebook
            ? Array.from(notebook.querySelectorAll<HTMLElement>('.ds-code-runner'))
                .slice(0, Array.from(notebook.querySelectorAll<HTMLElement>('.ds-code-runner')).indexOf(runner) + 1)
                .map((item) => item === runner
                  ? textarea.value
                  : item.querySelector<HTMLTextAreaElement>('.ds-code-input')?.value
                    || item.querySelector('pre code')?.textContent
                    || ''
                )
                .filter(Boolean)
                .join('\n\n')
            : textarea.value;
          const result = await api.executeCode(codeToRun);
          if (result.success) {
            output.textContent = result.output?.trim()
              || 'Code ran successfully, but it did not print anything. Add print(...) to inspect values.';
          } else {
            output.textContent = result.error || 'Execution failed.';
            output.classList.add('error');
          }
        } catch {
          output.textContent = 'Failed to execute code. Please try again.';
          output.classList.add('error');
        } finally {
          runButton.textContent = 'Run';
          runButton.removeAttribute('disabled');
        }
      });
    });

    root.querySelectorAll<HTMLElement>('.ds-quiz-choice').forEach((quiz) => {
      if (quiz.dataset.enhanced === 'true') return;
      quiz.dataset.enhanced = 'true';
      const explanation = quiz.querySelector<HTMLElement>('.ds-explanation');
      if (explanation) explanation.style.display = 'none';
      const feedback = document.createElement('p');
      feedback.className = 'ds-quiz-feedback';
      feedback.style.display = 'none';
      quiz.insertBefore(feedback, explanation || null);

      quiz.querySelectorAll<HTMLButtonElement>('.ds-option').forEach((option) => {
        option.addEventListener('click', () => {
          const selectedIsCorrect = option.dataset.correct === 'true';
          quiz.querySelectorAll<HTMLButtonElement>('.ds-option').forEach((item) => {
            item.classList.remove('correct', 'incorrect');
            item.disabled = true;
            if (!selectedIsCorrect && item.dataset.correct === 'true') item.classList.add('correct');
          });
          option.classList.add(selectedIsCorrect ? 'correct' : 'incorrect');
          feedback.textContent = selectedIsCorrect
            ? 'Correct.'
            : 'Not quite. The correct answer is highlighted.';
          feedback.classList.toggle('correct', selectedIsCorrect);
          feedback.classList.toggle('incorrect', !selectedIsCorrect);
          feedback.style.display = 'block';
          if (explanation) {
            if (!selectedIsCorrect) {
              explanation.textContent = explanation.textContent?.replace(/^Correct[!:\s-]*/i, '') || '';
            }
            explanation.style.display = 'block';
          }
        });
      });
    });
  }, [renderedContent]);

  const handleAskQuestion = async () => {
    if (!question.trim() || !popup.selectedText) return;

    setIsAsking(true);
    
    if (onAskQuestion) {
      onAskQuestion(popup.selectedText, question);
    }

    setPopup({ visible: false, x: 0, y: 0, selectedText: '' });
    setQuestion('');
    setIsAsking(false);
    window.getSelection()?.removeAllRanges();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAskQuestion();
    }
  };

  return (
    <div 
      ref={contentRef}
      className="relative"
      onMouseUp={handleSelection}
    >
      {/* HTML Content */}
      <div 
        className="ds-content"
        dangerouslySetInnerHTML={{ __html: renderedContent }}
      />

      {/* Selection Popup */}
      {popup.visible && (
        <div
          ref={popupRef}
          className="selection-popup animate-fade-in"
          style={{
            left: `${Math.min(Math.max(popup.x, 150), (contentRef.current?.offsetWidth || 400) - 150)}px`,
            top: `${Math.max(popup.y, 10)}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-apple-blue" />
            <span className="text-sm font-medium text-apple-text">Ask about this</span>
            <button
              onClick={() => setPopup(prev => ({ ...prev, visible: false }))}
              className="ml-auto p-1 hover:bg-apple-gray rounded"
            >
              <X className="w-3 h-3 text-apple-text-secondary" />
            </button>
          </div>
          
          <div className="max-w-xs mb-2">
            <p className="text-xs text-apple-text-secondary line-clamp-2 italic">
              "{popup.selectedText.substring(0, 100)}{popup.selectedText.length > 100 ? '...' : ''}"
            </p>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What's your question?"
              className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-apple-border rounded-lg focus:outline-none focus:border-apple-blue"
              autoFocus
            />
            <button
              onClick={handleAskQuestion}
              disabled={!question.trim() || isAsking}
              className="px-3 py-1.5 bg-apple-blue text-white text-sm rounded-lg hover:bg-apple-blue-hover disabled:opacity-50 transition-colors"
            >
              {isAsking ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <MessageSquare className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
